import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';

/**
 * Exported as a module singleton so non-React code (e.g. the Zustand auth
 * store's `logout()`) can call `queryClient.clear()` to purge cached data
 * across user sessions. The same instance is passed to `<QueryClientProvider>`
 * below, so the React tree and the singleton always agree.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // `gcTime` controls how long unmounted-query data lingers in
      // memory before being garbage-collected. TanStack's default is
      // 5 min, which keeps every navigated-away result around long
      // enough that a long browsing session (timeline scroll →
      // profile → blog → profile → search) can accumulate tens of
      // megabytes of cached payloads. 2 min still serves the common
      // back-button case (rehydrate from cache instantly when the
      // user pops back) while letting the rest go. Per-query
      // overrides above this (reference data like plans / categories /
      // geo country) raise gcTime explicitly where it matters.
      gcTime: 2 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function Providers({ children }: { children: ReactNode }) {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {/* `<Toaster />` is intentionally rendered INSIDE the router (in
            `AppRouter`) so a toast's `<Link>` / `useNavigate` (e.g. the
            LiveNotifier's "Watch" CTA) has the Router context it needs.
            Mounting it here would put it outside the BrowserRouter and
            crash with "Cannot destructure property 'basename' of
            useContext(...) as it is null". */}
        {children}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </HelmetProvider>
  );
}
